import React, { useState, useRef, useCallback, useEffect } from 'react';
import MentionAutocomplete from './MentionAutocomplete';
import compressImage from '../../lib/image_compressor';

// @context TGW-1769 入力欄。Enter送信/Shift+Enter改行（Slack準拠）、画像1枚（BE制約）。
//   @補完: 選択時に「@名前（空白除去）」を挿入し user_id を蓄積 → 送信時に mention_user_ids[] で渡す。
//   空白除去は表示装飾（decorateMentions の @[^\s]+ トークン）と一致させるため。
//   本文パースでの id 逆引きはしない（同姓同名・改名で壊れるため）。BE側でも正会員に再サニタイズされる。
export default function MessageComposer({
  circleId,
  channel,
  onSend,
  placeholder,
}) {
  const [body, setBody] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [mentionUsers, setMentionUsers] = useState([]); // {id, label}
  const [mentionChannel, setMentionChannel] = useState(false);
  const [mentionEveryone, setMentionEveryone] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null=補完閉、''以上=検索中
  const [candidates, setCandidates] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const debounceRef = useRef(null);

  // 集合メンション候補: @channel は常時、@everyone は全体チャンネルのみ（要件15）
  const groupOptions = [];
  if (mentionQuery !== null) {
    const q = mentionQuery.toLowerCase();
    if (!channel.isGeneral && 'channel'.startsWith(q)) {
      groupOptions.push({
        key: 'channel',
        isGroup: true,
        label: '@channel',
        description: 'チャンネルのメンバー全員に通知',
      });
    }
    if (channel.isGeneral && 'everyone'.startsWith(q)) {
      groupOptions.push({
        key: 'everyone',
        isGroup: true,
        label: '@everyone',
        description: 'サークル全員に通知',
      });
    }
  }

  // @入力の検出: caret 直前の「@クエリ」を拾う
  const detectMention = useCallback((value, caret) => {
    const before = value.slice(0, caret);
    const match = before.match(/(^|[\s\n])@([^\s@]*)$/);
    return match ? match[2] : null;
  }, []);

  useEffect(() => {
    if (mentionQuery === null) {
      setCandidates([]);
      return undefined;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/message/mention_candidates?circle_id=${circleId}&q=${encodeURIComponent(mentionQuery)}`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (res.ok) {
          const data = await res.json();
          setCandidates(data.candidates || []);
          setActiveIndex(0);
        }
      } catch (e) {
        setCandidates([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [mentionQuery, circleId]);

  const handleChange = (e) => {
    const { value, selectionStart } = e.target;
    setBody(value);
    setMentionQuery(detectMention(value, selectionStart));
  };

  const insertMention = (option) => {
    const el = textareaRef.current;
    const caret = el ? el.selectionStart : body.length;
    const before = body.slice(0, caret).replace(/@([^\s@]*)$/, '');
    const after = body.slice(caret);

    if (option.isGroup) {
      const token = `@${option.key} `;
      setBody(`${before}${token}${after}`);
      if (option.key === 'channel') setMentionChannel(true);
      if (option.key === 'everyone') setMentionEveryone(true);
    } else {
      // 空白除去した表示名で挿入（装飾トークンと一致させる）
      const token = `@${String(option.name || '').replace(/\s+/g, '')} `;
      setBody(`${before}${token}${after}`);
      setMentionUsers((prev) =>
        prev.some((u) => u.id === option.id)
          ? prev
          : [...prev, { id: option.id }],
      );
    }
    setMentionQuery(null);
    if (el) el.focus();
  };

  const popupOpen =
    mentionQuery !== null && groupOptions.length + candidates.length > 0;

  const handleKeyDown = (e) => {
    if (popupOpen) {
      const total = groupOptions.length + candidates.length;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % total);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + total) % total);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const all = [...groupOptions, ...candidates];
        insertMention(all[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  // @context TGW-1769 Phase2 DM側（message_send_form）と同じ送信前圧縮を適用。
  //   HEIC→JPEG変換と5MB超の圧縮をしないと、BEのサイズ/形式バリデーションで送信失敗する。
  const handleFileChange = async (e) => {
    const picked = e.target.files && e.target.files[0];
    if (!picked) return;
    const compressed = await compressImage(picked);
    setFile(compressed);
    setFilePreview(URL.createObjectURL(compressed));
  };

  const clearFile = () => {
    setFile(null);
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = () => {
    const trimmed = body.trim();
    if (!trimmed && !file) return;
    onSend({
      body: trimmed,
      file,
      mentionUserIds: mentionUsers.map((u) => u.id),
      mentionChannel,
      mentionEveryone,
    });
    setBody('');
    clearFile();
    setMentionUsers([]);
    setMentionChannel(false);
    setMentionEveryone(false);
    setMentionQuery(null);
  };

  return (
    <div className="slackch-composer">
      {popupOpen && (
        <MentionAutocomplete
          groupOptions={groupOptions}
          candidates={candidates}
          activeIndex={activeIndex}
          onSelect={insertMention}
        />
      )}
      <div className="slackch-composer-box">
        {filePreview && (
          <div className="slackch-composer-preview">
            <img src={filePreview} alt="添付画像" />
            <button type="button" onClick={clearFile} aria-label="添付を削除">
              ✕
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="slackch-composer-textarea"
          value={body}
          placeholder={placeholder}
          rows={2}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <div className="slackch-composer-actions">
          <button
            type="button"
            className="slackch-composer-icon"
            title="メンション"
            onClick={() => {
              setBody((v) => `${v}@`);
              setMentionQuery('');
              if (textareaRef.current) textareaRef.current.focus();
            }}
          >
            ＠
          </button>
          <label className="slackch-composer-icon" title="画像を添付">
            🖼️
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              hidden
            />
          </label>
          <button
            type="button"
            className="slackch-composer-send"
            onClick={submit}
            disabled={!body.trim() && !file}
          >
            送信
          </button>
        </div>
      </div>
    </div>
  );
}
